import { VehicleKind } from '@common/enums';
import { formatString } from '@common/utils';
import { FormikConfig } from 'formik';
import * as Yup from 'yup';

import { getFuelSetting, VehicleType, VehicleTypeFuelSetting } from '~/@store/vehicleTypes';
import { Fuel, VehicleTypeInput } from '~/graphql';
import i18n from '~/i18n';

export type IVehicleTypeFormValues = {
    id: string | null | undefined;
    name: string;
    externalId: string;
    beastId: number | null;
    kind: VehicleKind;
    fuelSettings: VehicleTypeFuelSetting[];
};

export function getVehicleTypeFormikConfig(
    vehicleType: VehicleType | null,
    handleSubmitFunc: (input: VehicleTypeInput) => Promise<VehicleType>,
    closeForm: () => void
): FormikConfig<IVehicleTypeFormValues> {
    return {
        initialValues: {
            id: vehicleType?.id,
            name: vehicleType?.name || '',
            kind: vehicleType?.kind || VehicleKind.ON_SITE,
            externalId: vehicleType?.externalId || '',
            beastId: vehicleType?.beastId || null,
            fuelSettings: [Fuel.Diesel, Fuel.HVO100, Fuel.Electric].map(
                fuel =>
                    (vehicleType && getFuelSetting(vehicleType, fuel)) || {
                        fuel,
                        consumption: 0,
                        consumptionPerHour: 0,
                        co2Emission: 0,
                    }
            ),
        },
        validationSchema: Yup.object().shape({
            id: Yup.string().nullable(),
            name: Yup.string().trim().required(formatString(i18n.isRequired, i18n.VehicleType.name)),
            kind: Yup.string()
                .oneOf(Object.values(VehicleKind))
                .required(formatString(i18n.isRequired, i18n.VehicleType.kind)),
            externalId: Yup.string().trim().required(formatString(i18n.isRequired, i18n.VehicleType.externalId)),
            beastId: Yup.number().nullable(),
            fuelSettings: Yup.array().of(
                Yup.object().shape({
                    consumption: Yup.number().min(0),
                    co2Emission: Yup.number().min(0),
                })
            ),
        }),
        onSubmit: async (values, { resetForm }) => {
            const { name, kind, externalId, fuelSettings, beastId } = values;

            const input: VehicleTypeInput = {
                name,
                kind,
                externalId,
                beastId,
                fuelSettings: fuelSettings.map(fs => ({
                    fuel: fs.fuel,
                    consumption: fs.consumption,
                    consumptionPerHour: fs.consumptionPerHour,
                    co2Emission: fs.co2Emission,
                })),
            };

            await handleSubmitFunc(input);
            vehicleType ? resetForm() : closeForm();
        },
    };
}
